import * as Location from "expo-location";
import { Platform } from "react-native";

import { cachedFixIsFreshEnough, fixAgeMs } from "@/lib/fix-age";
import { withTimeout } from "@/lib/with-timeout";

export type Coords = {
  lat: number;
  lng: number;
  /**
   * Radius of uncertainty of this fix, in metres, as the device reported it.
   *
   * Carried all the way to the server, which will not apply a radius rule to a
   * coordinate vaguer than the radius itself — an approximate grant is
   * uncertain to a kilometre or more, which is wider than the whole discovery
   * range. Null when the platform did not report one, which the server treats
   * the same as too wide.
   */
  accuracyMeters: number | null;
  /**
   * How old this fix already was when it was read, in milliseconds.
   *
   * Usually ~0. It is not when the live read times out and `getCurrentCoords`
   * falls back to `getLastKnownPositionAsync`, which will hand back a position
   * up to five minutes old — the ordinary indoor and cold-resume case. Without
   * this the server stamped every arriving fix as taken "now", so a heartbeat
   * kept a five-minute-old coordinate looking permanently fresh.
   *
   * An AGE and not a timestamp on purpose: both halves of the subtraction come
   * from the same device clock, so a phone whose clock is wrong still reports a
   * correct age. See `coordinate-freshness.ts` on the server.
   */
  ageMs: number;
};

export type LocationResult =
  | { status: "ok"; coords: Coords }
  | { status: "denied" }
  | { status: "device_location_off" }
  | { status: "unavailable" };

/**
 * How exact the position we are allowed to take is.
 *
 * Android lets someone grant "approximate" instead of "precise", which is
 * roughly city-block to neighbourhood level. That is enough to tell whether
 * someone is near a place, so opncue runs on it rather than treating it as a
 * refusal — precise is asked for separately, and only where it changes what the
 * app can do.
 */
export type LocationPrecision = "fine" | "coarse" | "none";

/**
 * `getCurrentPositionAsync` has no timeout of its own and will wait forever for
 * a fix it may never get — an approximate-only grant indoors is the usual way
 * in. Without this the screen sits on "Loading your space" with no way out.
 *
 * Two budgets, because how long it is worth waiting depends on whether there is
 * anything to fall back on. With a usable cached fix in hand a fresh read is an
 * improvement, not a requirement, so we stop waiting for it much sooner.
 */
const POSITION_TIMEOUT_MS = 12000;
const POSITION_TIMEOUT_WITH_FALLBACK_MS = 4000;

/**
 * How old a cached fix may be, and how imprecise, and still stand in for a
 * fresh one.
 *
 * The cached read exists because a live fix "may take several seconds,
 * especially inside a building" — expo-location's own words, and a café is
 * inside a building. Declining to use it would not buy fresher data, it would
 * buy no data.
 *
 * The accuracy ceiling is pinned to `NEARBY_RADIUS_MIN_M`, not chosen
 * separately. It was 1000 m against a 200 m floor, which meant the OS could hand
 * back a fix no radius setting could use — and once visibility requires a usable
 * fix, that is a refusal with location fully granted and working, and nothing
 * the user can do about it. Matching the two buys the invariant that the client
 * never offers a fix that no radius could accept.
 *
 * It is a LOCAL mirror on purpose: `common/nearby-radius.ts` is server code and
 * this module must stay loadable by the root `node --test` suites. The server
 * clamps regardless, so a drifted copy can only make this client fussier than
 * necessary, never laxer.
 */
const LAST_KNOWN_MAX_AGE_MS = 5 * 60 * 1000;
const LAST_KNOWN_MAX_ACCURACY_M = 500;

export type CoordsOptions = {
  /**
   * Serve from the OS's cached fix when it is no older than this, instead of
   * paying for a live read.
   *
   * Omitted or zero — the default, and what every user-initiated path uses —
   * always attempts a live read. Only the presence heartbeat passes a value:
   * it runs unattended every couple of minutes, and forcing the location stack
   * awake each time to re-learn that somebody is still at their table is the
   * single most expensive thing the app does while simply sitting open.
   *
   * Safe only because the fix's age travels to the server with it. Preferring
   * the cache without that would be the stale-coordinate bug by choice.
   */
  acceptCachedWithinMs?: number;
};

/**
 * Resolve the device's current coordinates.
 *
 * Never prompts. Asking is the permissions screen's job, so no data refresh can
 * be the thing that puts an OS dialog on screen — a permission request that
 * arrives out of a background refresh has no explanation attached to it.
 */
export async function getCurrentCoords(
  options: CoordsOptions = {},
): Promise<LocationResult> {
  try {
    const { granted } = await Location.getForegroundPermissionsAsync();
    if (!granted) return { status: "denied" };

    const servicesEnabled = await Location.hasServicesEnabledAsync();
    if (!servicesEnabled) return { status: "device_location_off" };

    // Read first and effectively free — the OS already has this. Knowing
    // whether there is a fallback is what sets the budget below.
    const lastKnown = await Location.getLastKnownPositionAsync({
      maxAge: LAST_KNOWN_MAX_AGE_MS,
      requiredAccuracy: LAST_KNOWN_MAX_ACCURACY_M,
    }).catch(() => null);

    // The battery short-circuit. A caller that has said how stale an answer it
    // can live with, and already holds one that fresh, stops here — no live
    // read, no location stack, no 4-second wait. Self-correcting by
    // construction: once the cached fix ages past the caller's threshold the
    // next call falls through and refreshes it, so the cache cannot rot.
    if (
      lastKnown &&
      cachedFixIsFreshEnough(
        lastKnown.timestamp,
        options.acceptCachedWithinMs ?? 0,
      )
    ) {
      return toResult(lastKnown);
    }

    const position = await withTimeout(
      Location.getCurrentPositionAsync({
        // WEB ONLY, and load-bearing. `expo-location`'s web shim calls
        // `navigator.geolocation.getCurrentPosition` with `maximumAge:
        // Infinity` and spreads the caller's options over it, so without this
        // key the browser is explicitly told "any cached position will do,
        // however old" — and returns one instantly. On web this function was
        // therefore never a live read at all.
        //
        // Measured: after a tab had been backgrounded, the whole
        // available -> venues/current -> join burst completed in 17 ms with a
        // fix old enough that the server clamped it to
        // MAX_REPORTED_FIX_AGE_MS and the very next read refused it as stale.
        // No timeout was involved, which is what rules out the slow-fix story.
        //
        // Native ignores this key, and its own cache decision is the
        // `acceptCachedWithinMs` short-circuit above.
        ...(Platform.OS === "web"
          ? { maximumAge: options.acceptCachedWithinMs ?? 0 }
          : {}),
        // Deliberately not Highest. On Android, asking for a precise fix while
        // holding only the approximate permission makes the system throw up its
        // "change to precise?" dialog mid-session, and then usually fails to
        // produce a position at all when the answer is no. Balanced is ~100m,
        // which the approximate grant can satisfy and which is well inside the
        // ranges opncue actually works at.
        accuracy: Location.Accuracy.Balanced,
      }),
      lastKnown ? POSITION_TIMEOUT_WITH_FALLBACK_MS : POSITION_TIMEOUT_MS,
    );

    // A slow fix is not the same as not knowing where someone is. Falling back
    // is what stops a cold read — the ordinary case on resume, or indoors —
    // from being reported as a location failure.
    const fix = position ?? lastKnown;
    if (!fix) return { status: "unavailable" };

    return toResult(fix);
  } catch {
    return { status: "unavailable" };
  }
}

/**
 * One place that turns a fix into an answer, so the cached short-circuit above
 * and the live path below cannot describe the same position differently — in
 * particular cannot disagree about its age, which is the field the server uses
 * to decide how much to trust it.
 */
function toResult(fix: Location.LocationObject): LocationResult {
  return {
    status: "ok",
    coords: {
      lat: fix.coords.latitude,
      lng: fix.coords.longitude,
      accuracyMeters: fix.coords.accuracy ?? null,
      // Read off the fix we actually used, which may be the cached one — up to
      // five minutes old. Without this the server has no way to tell that apart
      // from a live read and dates both "now".
      ageMs: fixAgeMs(fix.timestamp),
    },
  };
}

/** Read-only: what we currently hold. Never prompts. */
export async function getLocationPrecision(): Promise<LocationPrecision> {
  try {
    const permission = await Location.getForegroundPermissionsAsync();
    if (!permission.granted) return "none";

    // Only Android splits the grant. Everywhere else, granted means precise.
    if (Platform.OS !== "android") return "fine";
    return permission.android?.accuracy === "coarse" ? "coarse" : "fine";
  } catch {
    return "none";
  }
}

/**
 * Ask to upgrade approximate to precise. Only call this from a control the user
 * pressed, next to an explanation of what it buys them — the system dialog
 * gives no reason of its own.
 */
export async function requestPreciseLocation(): Promise<LocationPrecision> {
  try {
    await Location.requestForegroundPermissionsAsync();
  } catch {
    // Fall through to whatever we ended up with.
  }
  return getLocationPrecision();
}
